import React, { useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl } from '../../util/reactIntl';
import { handleAddToCart } from '../../containers/ListingPage/ListingPage.shared';
import { fetchCartListings, toggleCart } from '../../containers/CartPage/CartPage.duck';

import css from './ListingCard.module.css';

// The same cart (trolley) as the header's cart icon, so it reads as the same
// cart. Once the book is in it, a small wine tick badge sits on its corner.
const IconCart = ({ inCart }) => (
  <svg className={css.quickAddIcon} viewBox="0 0 24 24" aria-hidden="true">
    <g
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </g>
    {inCart ? (
      <g className={css.quickAddTick}>
        <circle cx="18.5" cy="5.5" r="5.5" />
        <path
          d="M16 5.6l1.7 1.7 3-3.2"
          fill="none"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    ) : null}
  </svg>
);

/**
 * Adds a book to the cart from its card, without opening the listing page.
 * Tapping again takes it back out. Uses the same checks as the listing page's
 * Add to cart: signed-out visitors go to sign up, and accounts that can't buy
 * yet go to the page explaining why.
 *
 * Hidden on the viewer's own listings and on books known to be out of stock.
 *
 * @param {Object} props
 * @param {Object} props.listing the card's listing, with its author
 */
const QuickAddButton = props => {
  const { listing } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const config = useConfiguration();
  const routes = useRouteConfiguration();
  const currentUser = useSelector(state => state.user.currentUser);
  const inProgress = useSelector(state => state.CartPage?.toggleCartInProgress);
  const [error, setError] = useState(null);

  const listingId = listing?.id?.uuid;
  const authorId = listing?.author?.id?.uuid;
  const cart = currentUser?.attributes?.profile?.privateData?.cart || {};
  const countInCart = cart[authorId]?.[listingId]?.count || 0;
  const inCart = countInCart > 0;

  const stock = listing?.currentStock?.attributes?.quantity;
  const isOwnListing = !!authorId && authorId === currentUser?.id?.uuid;
  const isSoldOut = typeof stock === 'number' && stock <= 0 && !inCart;
  if (!listingId || !authorId || isOwnListing || isSoldOut) {
    return null;
  }

  const onToggleCart = (id, sellerId, increment) =>
    dispatch(toggleCart(id, sellerId, increment)).then(
      // Pass the config so the refresh keeps the card-sized photos: without it
      // the cart reloads these books with thumbnails only, and their cards
      // fall back to "NO IMAGE".
      () => dispatch(fetchCartListings(config)),
      e => setError(e?.message || 'error')
    );

  const handleClick = e => {
    // The whole card is a link to the listing page.
    e.preventDefault();
    e.stopPropagation();
    if (inProgress) {
      return;
    }
    setError(null);
    handleAddToCart({ currentUser, listing, history, location, routes, onToggleCart })(
      inCart ? -countInCart : 1
    );
  };

  const label = intl.formatMessage({
    id: inCart ? 'ListingCard.quickRemove' : 'ListingCard.quickAdd',
  });

  return (
    <button
      type="button"
      className={classNames(css.quickAdd, { [css.quickAddInCart]: inCart })}
      onClick={handleClick}
      aria-pressed={inCart}
      aria-label={label}
      title={error ? intl.formatMessage({ id: 'ListingCard.quickAddFailed' }) : label}
    >
      <IconCart inCart={inCart} />
    </button>
  );
};

export default QuickAddButton;
